import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Settings, RefreshCw, ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { API_BASE_URL } from '@/client/config';
import { Button } from '@/client/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '@/client/components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/client/components/ui/collapsible';
import { Badge } from '@/client/components/ui/badge';
import { cn } from '@/client/lib/utils';
import type { ConfigItem, ConfigGroup } from '@shared/types';

interface ConfigResponse {
  groups: ConfigGroup[];
}

export function ConfigSheet() {
  const { getAccessTokenSilently } = useAuth0();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<ConfigGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showSensitive, setShowSensitive] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`);
      }
      const data: ConfigResponse = await response.json();
      setConfig(data.groups);
      // Expand all groups by default
      setExpandedGroups(new Set(data.groups.map((g) => g.name)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch config when sheet opens
  useEffect(() => {
    if (open && config.length === 0) {
      fetchConfig();
    }
  }, [open]);

  const toggleGroup = (name: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(config.map((g) => g.name)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const formatValue = (item: ConfigItem): string => {
    if (item.sensitive && !showSensitive) {
      return '••••••••';
    }
    if (item.displayValue) {
      return item.displayValue;
    }
    if (typeof item.value === 'boolean') {
      return item.value ? 'true' : 'false';
    }
    return String(item.value);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" title="View app configuration">
          <Settings className="h-4 w-4 mr-2" />
          Config
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            App Configuration
          </SheetTitle>
          <SheetDescription>
            All configuration values currently in use
          </SheetDescription>
        </SheetHeader>

        {/* Toolbar */}
        <div className="flex items-center gap-2 py-2 border-b">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchConfig}
            disabled={loading}
          >
            <RefreshCw className={cn('h-4 w-4 mr-1', loading && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSensitive(!showSensitive)}
          >
            {showSensitive ? (
              <EyeOff className="h-4 w-4 mr-1" />
            ) : (
              <Eye className="h-4 w-4 mr-1" />
            )}
            {showSensitive ? 'Hide' : 'Show'} Sensitive
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          {loading && config.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Loading configuration...
            </div>
          )}

          {config.map((group) => (
            <Collapsible
              key={group.name}
              open={expandedGroups.has(group.name)}
              onOpenChange={() => toggleGroup(group.name)}
            >
              <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 hover:bg-muted rounded-md">
                {expandedGroups.has(group.name) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                <span className="font-medium">{group.name}</span>
                <Badge variant="secondary" className="ml-auto">
                  {group.items.length}
                </Badge>
              </CollapsibleTrigger>
              <CollapsibleContent>
                {group.description && (
                  <p className="text-xs text-muted-foreground px-6 pb-2">
                    {group.description}
                  </p>
                )}
                <div className="pl-6 pr-2 space-y-1">
                  {group.items.map((item) => (
                    <div
                      key={item.key}
                      className="flex items-start gap-2 py-1.5 text-sm border-b border-dashed last:border-0"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs text-muted-foreground">
                          {item.key}
                          {item.sensitive && (
                            <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">
                              sensitive
                            </Badge>
                          )}
                        </div>
                        <div className="font-medium truncate" title={String(item.value)}>
                          {formatValue(item)}
                        </div>
                        {item.description && (
                          <div className="text-xs text-muted-foreground">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t pt-3 text-xs text-muted-foreground">
          {config.length > 0 && (
            <span>
              {config.length} groups, {config.reduce((acc, g) => acc + g.items.length, 0)} items
            </span>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
